import { ADMIN_CURATOR, PROJECT_RENDER } from '../config/constant/routePathConstant';
import pluralize from 'pluralize';

import {
  ADD_PRODUCT_CONFIGURATOR_ROUTE,
  ADMIN_MY_PRODUCTS_ROOMS,
  ADMIN_MY_PRODUCTS_ROOMS_V2,
  ADMIN_MY_PROJECTS_V2,
  ADMIN_SETTINGS,
  COLLECTIONS_ROUTE,
  COLLECTION_ID,
  COLLECTION_RENDER_ROUTE,
  COLLECTION_TEMPLATE_ROUTE,
  MY_PROJECTS_ROUTE,
  NEW_DESIGN_ROUTE,
  PRODUCT_CONFIGURATORS_ROUTE,
  PRODUCT_CONFIGURATORS_ROUTE_V2,
  PRODUCT_CONFIGURATOR_ROUTE,
  RENDER_DETAILS,
  RENDER_DETAILS_V2,
  RETAIL_AUTOMATION_DETAILS_ROUTE,
  SHARED_FILES_IMAGES_ROUTE,
  MY_FILES_SHARED_FILES_IMAGES_ROUTE,
  SHARED_FILES_ROUTE,
  SHARED_FILES_TEMPALTE_ROUTE,
  MY_FILES_SHARED_FILES_TEMPALTE_ROUTE,
  TEMPLATES_ROUTE,
  TEMPLATES_ROUTE_V2,
  TRASH_ROUTE,
  CURATOR_PRODUCT_BUILDER_ROUTE,
  PRODUCT_BUILDER_ROUTE,
  RETAIL_AUTOMATION_DETAILS_ROUTE_V2,
} from '../constants/routePathConstants';
import { OPEN_PROJECT_ID } from '../config/constant/curatorConstants';
import { ROOM_TYPE__URL_SLUG_MAP } from '../constants/filters';
import { FAILED_REASON, SETTINGS_TAB } from '../constants/constants';
import { history } from '../history';
import { URL_VALIDATION } from '../constants/validationConstants';

export const getProjectPath = ({ id }) => {
  return PROJECT_RENDER.replace(':projectId', id);
};

export const getCuratorPath = (
  id,
  type,
  { cameraName, propId, zipUuid, autosaveId, automationId, loadExternalJson } = {}
) => {
  let item = pluralize.singular(type);
  if (autosaveId && (id === OPEN_PROJECT_ID || !id)) {
    item = 'room';
  }

  if (automationId && (id === OPEN_PROJECT_ID || !id)) {
    item = 'room';
  }

  let path = `${ADMIN_CURATOR}/${item}/${id}`;

  const query = [
    cameraName && `cameraName=${cameraName}`,
    propId && `propId=${propId}`,
    zipUuid && `zipUuid=${zipUuid}`,
    autosaveId && `autosaveId=${autosaveId}`,
    automationId && `automationId=${automationId}`,
    loadExternalJson && `loadExternalJson=${loadExternalJson}`,
  ]
    .filter(Boolean)
    .join('&');

  return `${path}?${query}`;
};

export const getCuratorRoomPath = (id, { cameraName, propId, zipUuid } = {}) =>
  getCuratorPath(id, 'room', { cameraName, propId, zipUuid });
export const getCuratorProjectPath = (id, autosaveId) =>
  getCuratorPath(id, 'project', { autosaveId });

export const getCuratorAutomationPath = (id, automationId) =>
  getCuratorPath(id, 'room', { automationId });

export const getCuratorRenderPreviewPath = (
  renderId,
  cameraName,
  isAutomation = false,
  combinedJson
) => {
  let query = `renderId=${renderId}`;
  if (cameraName) {
    query += `&cameraName=${cameraName}`;
  }
  if (isAutomation) {
    query += `&automation=${isAutomation}`;
  }
  if (combinedJson) {
    query = `combinedWithConfiguratorId=${renderId}`;
  }
  return getCuratorPath(OPEN_PROJECT_ID, 'room') + query;
};

// Retrieves the preview path for a mattress curator render.
export const getCuratorRenderPreviewPathForMattress = ({
  data = null,
  fallbackUrl = null,
} = {}) => {
  if (!data) return fallbackUrl;

  const url = getProductBuilderPath(data?.project?.[0]);

  // Check if it's a product builder and the type is '9' (Silo template).
  // which is coming only when we render the image from the mattress builder project without selecting template
  // If both conditions are true and a valid URL is obtained, return the URL
  // Otherwise, return the fallback url
  if (data?.is_product_builder && data?.type === '9') {
    if (!data?.project?.[0]) return '';
    return url;
  }
  return fallbackUrl;
};

export function removeParams(path) {
  return path
    .split('/')
    .filter((x) => x.charAt(0) !== ':')
    .join('/');
}

export const getProjectsPath = (isGuestLogin) => {
  return isGuestLogin || window.$is_guest_user ? TEMPLATES_ROUTE_V2 : ADMIN_MY_PROJECTS_V2;
};

export const getMattressBuilderPath = () => {
  return PRODUCT_BUILDER_ROUTE;
}

export const openCuratorRoom = (roomId, { cameraName, propId, zipUuid } = {}) => {
  window.open(getCuratorRoomPath(roomId, { cameraName, propId, zipUuid }), '_blank');
};

export const getNewDesignPath = (roomType) => {
  const slug = ROOM_TYPE__URL_SLUG_MAP[roomType];
  return NEW_DESIGN_ROUTE.replace(':id', slug);
};

export const getRenderDetailPath = (renderId) => {
  return RENDER_DETAILS_V2.replace(':id', renderId);
};

export const getTransactionsPath = () => `${ADMIN_SETTINGS}?activeTab=${SETTINGS_TAB.TRANSACTIONS}`;

export const getFailedReason = (key) => {
  if (!key) return key;
  return FAILED_REASON[Object.keys(FAILED_REASON).find((a) => a === key.toString())];
};

export const getMultipleResponseList = (sourceObject, targetList, action = '') => {
  if (!sourceObject) return [];
  if (!Object.keys(sourceObject).length && !targetList?.length) return [];

  const list = Object.keys(sourceObject).map((a) => {
    const { id, name } = targetList.find((b) => b.id.toString() === a.toString());
    return {
      id: id,
      name: name,
      status: sourceObject?.[a]?.deleted || sourceObject?.[a]?.updated || sourceObject?.[a]?.status,
      reason: sourceObject?.[a]?.reason,
      reasonData: sourceObject?.[a]?.reason_data,
      action: action || sourceObject?.[a]?.action,
    };
  });

  return list;
};

export const getCollectionRenderPath = (collectionId, renderId) => {
  return COLLECTION_RENDER_ROUTE.replace(':collectionId', collectionId).replace(
    ':renderId',
    renderId
  );
};

export const getCollectionPath = (collectionId, { params = '' }) => {
  return COLLECTION_ID.replace(':id', collectionId) + `?` + params;
};

export const getCollectionTemplatePath = (collectionId, roomId) => {
  return COLLECTION_TEMPLATE_ROUTE.replace(':collectionId', collectionId).replace(
    ':roomId',
    roomId
  );
};

export const getSharedFilesRenderPath = (renderId) => {
  return MY_FILES_SHARED_FILES_IMAGES_ROUTE.replace(':renderId', renderId);
};

export const getSharedFilesTemplatePath = (roomId) => {
  return MY_FILES_SHARED_FILES_TEMPALTE_ROUTE.replace(':roomId', roomId);
};

export const isCuratorPage = () => window.location.pathname.includes('curator');

export const syncFiltersIntoQuery = (urlParams) => {
  const s = window.location.search;
  const s1 = s.startsWith('?') ? s : `?${s}`;
  const s2 = `?${urlParams}`;

  if (s1 !== s2) {
    // console.log('this will push url')
    const url = location.pathname + '?' + urlParams;
    history.push(url);
  }
};

// source must be an array of object
export const findInObjectThenAddOrUpdate = (source = [], key, value = {}) => {
  let result = [...source];
  const index = source.findIndex((a) => Object.keys(a)[0] === key);
  const updatedValue = Array.isArray(value) ? { [key]: value } : value;

  if (index !== -1) {
    result[index] = updatedValue;
  } else {
    result.push(updatedValue);
  }

  return result;
};

export const getParamsFromFilterBy = (value, key = '') => {
  const param = {};
  if ('key' in value) {
    if (value.key) {
      param[value.key] = value.apiValue;
    } else {
      if (key) param[key] = value.value;
    }
  } else {
    if (key) param[key] = value.value;
  }

  return param;
};

export const getFilterByKeyFromPage = () => {
  const url = window.location.pathname;
  let key = '';

  if (url.includes(MY_PROJECTS_ROUTE) || url.includes(ADMIN_MY_PROJECTS_V2)) {
    key = 'project_type';
  } else if (
    url.includes(TEMPLATES_ROUTE) ||
    url.includes(ADMIN_MY_PRODUCTS_ROOMS) ||
    url.includes(ADMIN_MY_PRODUCTS_ROOMS_V2) ||
    url.includes('/newdesigns')
  ) {
    key = 'room_type';
  } else if (url.includes(COLLECTIONS_ROUTE) || url.includes(TRASH_ROUTE)) {
    key = 'model_type';
  } else if (url.includes(SHARED_FILES_ROUTE)) {
    key = 'type';
  } else if (
    url.includes(PRODUCT_CONFIGURATORS_ROUTE) ||
    url.includes(PRODUCT_CONFIGURATORS_ROUTE_V2)
  ) {
    if (url.includes('scenes') && url.includes('views')) {
      key = 'room_type';
    } else {
      key = 'config_type';
    }
  }
  return key;
};

export const getUpdatedValueFromFiltersValue = (value, params) => {
  const lastIndex = value.lastIndexOf('_');
  let key = value.slice(0, lastIndex);
  const val = value.slice(lastIndex + 1);

  if (key.includes('option')) {
    key = key.replace('option', 'fl');
  }
  if (key.includes('collection') || key.includes('brand') || key.includes('prop')) {
    key = key.replace(key, `${key}s`);
  }

  let updatedVal = [];

  const i = params.findIndex((a) => Object.keys(a)[0] === key);
  if (i !== -1) {
    updatedVal = params[i][key];
    if (!Array.isArray(updatedVal)) {
      updatedVal = [updatedVal];
    }
    if (updatedVal?.length && updatedVal?.includes(val)) {
      updatedVal = updatedVal?.filter((a) => a !== val);
    } else {
      updatedVal = [...updatedVal, val];
    }
  } else {
    updatedVal = [...updatedVal, val];
  }

  return {
    key,
    updatedVal,
  };
};

export const getUpdatedValueFromRemovedFiltersValue = (value, params) => {
  const lastIndex = value.lastIndexOf('_');
  let key = value.slice(0, lastIndex);
  const val = value.slice(lastIndex + 1);

  if (key.includes('option')) {
    key = key.replace('option', 'fl');
  }
  if (key.includes('collection') || key.includes('brand') || key.includes('prop')) {
    key = key.replace(key, `${key}s`);
  }

  let updatedVal = [];
  const i = params.findIndex((a) => Object.keys(a)[0] === key);
  if (i !== -1) {
    updatedVal = params[i][key];

    if (updatedVal.includes(val)) {
      updatedVal = updatedVal.filter((a) => a !== val);
    }
  }

  return {
    key,
    updatedVal,
  };
};

export const isValidHttpURL = (urlParams) => {
  return URL_VALIDATION.test(urlParams);
};

export const getConfigruatorPath = (id) => {
  return PRODUCT_CONFIGURATOR_ROUTE.replace(':configuratorId', id).replace('/*', '');
};

export const getNewConfiguratorPath = (type) => {
  return ADD_PRODUCT_CONFIGURATOR_ROUTE.replace('/*', '') + `?type=${type}`;
};

export const getNewConfiguratorPathV2 = () => {
  return ADD_PRODUCT_CONFIGURATOR_ROUTE.replace('/*', '');
};

export const getRetailAutomationPath = (id) => {
  // RETAIL_AUTOMATION_DETAILS_ROUTE
  return RETAIL_AUTOMATION_DETAILS_ROUTE_V2.replace(':automationId', id).replace('/*', '')
}
export const isProductionUrl = window.location.origin?.includes('//app.imagine.io')

export const getProductBuilderPath = (id) => {
  return id ? CURATOR_PRODUCT_BUILDER_ROUTE.replace(':id', id).replace('/*', '') : null;
}